import React from "react";
import Link from "next/link";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Meta from "../Meta";
import Loader from "../loader";
import { ASSET_URL } from "../../../config";
import { applyLang } from "../../../i18n";
import renderIcon from "../../../components/renderIcon";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { resolveImage } from "../ui-components/_shared";
import { useEvents } from "../../../services/EventService";

// Utils date — alignés avec EventsStrip pour cohérence visuelle.
function toMs(v) {
    if (!v) return null;
    const ms = typeof v === "number" ? v : Date.parse(v);
    return isNaN(ms) ? null : ms;
}

function startOfToday() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
}

function formatDay(ms) {
    return new Date(ms).getDate();
}

function formatMonth(ms, lang) {
    return new Date(ms)
        .toLocaleDateString(lang || "fr", { month: "short" })
        .replace(".", "")
        .toUpperCase();
}

function formatPeriod(startMs, endMs, lang) {
    if (!startMs) return null;
    const start = new Date(startMs);
    const opts = { weekday: "long", day: "numeric", month: "long" };
    const timeOpts = { hour: "2-digit", minute: "2-digit" };
    const hasTime = start.getHours() !== 0 || start.getMinutes() !== 0;
    const timeStr = hasTime ? start.toLocaleTimeString(lang || "fr", timeOpts) : null;

    if (!endMs || Math.abs(endMs - startMs) < 24 * 3600 * 1000) {
        const dateStr = start.toLocaleDateString(lang || "fr", opts);
        return timeStr ? `${dateStr} · ${timeStr}` : dateStr;
    }
    const end = new Date(endMs);
    const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
    if (sameMonth) {
        const monthYear = end.toLocaleDateString(lang || "fr", { month: "long", year: "numeric" });
        return `${start.getDate()} → ${end.getDate()} ${monthYear}`;
    }
    const startStr = start.toLocaleDateString(lang || "fr", { day: "numeric", month: "short" });
    const endStr = end.toLocaleDateString(lang || "fr", { day: "numeric", month: "long", year: "numeric" });
    return `${startStr} → ${endStr}`;
}

// Styles repris d'EventsStrip pour les cards (cohérence visuelle avec la home),
// + styles propres à la page liste (titre, empty state, grille élargie).
const eventsPageStyles = `
.tr-events-page__title {
  font-size: clamp(24px, 4vw, 40px);
  font-weight: 800;
  color: #1a1a2e;
  text-align: center;
  margin: 56px 0 32px;
  padding: 0 16px;
}
.tr-events-page__list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 420px));
  gap: clamp(16px, 2vw, 24px);
  justify-content: center;
  max-width: 1100px;
  margin: 0 auto 56px;
  padding: 0 16px;
}
@media (max-width: 560px) {
  .tr-events-page__list { grid-template-columns: minmax(0, 420px); }
}
.tr-events-page__empty {
  text-align: center;
  padding: 64px 16px;
  color: #6b7280;
  font-size: 15px;
}

/* Card — repris d'EventsStrip */
.tr-events__card {
  display: flex;
  flex-direction: column;
  border-radius: 18px;
  overflow: hidden;
  background: white;
  border: 1px solid #e5e7eb;
  text-decoration: none;
  color: inherit;
  transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
}
.tr-events__card:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 32px rgba(0,0,0,.1);
  border-color: #d1d5db;
}
.tr-events__imgwrap {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #f3f4f6;
  overflow: hidden;
}
.tr-events__img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  transition: transform .4s ease;
}
.tr-events__card:hover .tr-events__img { transform: scale(1.04); }
.tr-events__img-placeholder {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  color: var(--primary, #1b579e);
  background: color-mix(in srgb, var(--primary, #1b579e) 8%, white);
}
.tr-events__datebox {
  position: absolute;
  top: 12px;
  inset-inline-start: 12px;
  background: white;
  border-radius: 12px;
  padding: 8px 10px 6px;
  min-width: 54px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0,0,0,.12);
  line-height: 1;
}
.tr-events__datebox__day {
  display: block;
  font-size: 22px;
  font-weight: 800;
  color: #1a1a2e;
}
.tr-events__datebox__month {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  font-weight: 600;
  color: var(--primary, #1b579e);
  letter-spacing: .05em;
}
.tr-events__live {
  position: absolute;
  top: 12px;
  inset-inline-end: 12px;
  background: #dc2626;
  color: white;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
}
.tr-events__body {
  display: flex; flex-direction: column; gap: 10px;
  padding: 18px 18px 20px;
  flex: 1;
}
.tr-events__name {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #1a1a2e;
  line-height: 1.25;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.tr-events__meta {
  display: flex; flex-direction: column; gap: 6px;
}
.tr-events__meta-row {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 13px;
  color: #4b5563;
}
.tr-events__desc {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.45;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}
.tr-events__links {
  display: flex; flex-wrap: wrap; gap: 8px;
  margin-top: 4px;
}
.tr-events__link {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--primary, #1b579e) 10%, white);
  color: var(--primary, #1b579e);
  cursor: pointer;
  text-decoration: none;
  user-select: none;
}
.tr-events__more {
  display: inline-flex; align-items: center; gap: 6px;
  margin-top: auto;
  font-size: 13px;
  font-weight: 600;
  color: var(--primary, #1b579e);
}
.tr-events__card:hover .tr-events__more { text-decoration: underline; }
`;

export default function Events({ context, loading }) {
    const { org, lang, config } = context;
    // Variante « Fêtes » (page /travel/fetes) : même rendu, mais filtré sur les
    // événements préfabriqués (prefabKey posé par le système prefab_events).
    const isFetes = context.page?.type === "TRAVEL.FETES";
    // Événements fetchés CÔTÉ CLIENT via /v1/events (modèle galeries/actualités).
    const { events: allEvents } = useEvents(context);
    const todayStart = startOfToday();
    const nowMs = Date.now();

    // Même règle de visibilité qu'EventsStrip : actif, non masqué, fin >= aujourd'hui.
    // Pas de limite ici : la page liste tout. Événements issus de /v1/events.
    const events = (allEvents || [])
        .filter(e => e && e.active !== false && !e.hidden)
        .filter(e => !isFetes || e.prefabKey)
        .map(e => {
            const startMs = toMs(e.dateEvent || e.date_start || e.date);
            const endMs = toMs(e.dateFin || e.date_end) || startMs;
            return { e, startMs, endMs };
        })
        .filter(({ startMs, endMs }) => {
            if (!startMs) return false;
            return (endMs || startMs) >= todayStart;
        })
        .sort((a, b) => (a.startMs || 0) - (b.startMs || 0));

    const cityName = org.ville || org.name;

    // JSON-LD ItemList pour aider Google à comprendre la liste des événements.
    const itemListElements = events.slice(0, 50).map(({ e: ev, startMs, endMs }, i) => {
        const loc = applyLang(ev, lang);
        const url = `${org.BASE_WEB_URL}/evenement/${ev.urlSlug || ev._id || ev.id}`;
        return {
            "@type": "ListItem",
            position: i + 1,
            item: {
                "@type": "Event",
                name: loc.name || loc.title,
                url,
                startDate: startMs ? new Date(startMs).toISOString() : undefined,
                endDate: endMs ? new Date(endMs).toISOString() : undefined,
                image: ev.image ? (ev.image.startsWith("http") ? ev.image : `${ASSET_URL}${ev.image}`) : undefined,
                location: loc.address?.address ? {
                    "@type": "Place",
                    address: { "@type": "PostalAddress", streetAddress: loc.address.address },
                } : undefined,
            },
        };
    });
    const pageTitle = isFetes ? t`Fêtes juives à ${cityName}` : t`Événements à ${cityName}`;
    const jsonLd = itemListElements.length > 0 ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: pageTitle,
        itemListElement: itemListElements,
    } : null;

    return (
        <div className="travel-page travel-page--events">
            <style dangerouslySetInnerHTML={{ __html: eventsPageStyles }} />
            <Meta
                context={context}
                title={pageTitle}
                description={
                    config?.["meta.description"] ||
                    (isFetes
                        ? t`Les fêtes juives et leurs inscriptions à ${cityName}`
                        : t`Tous les événements à venir à ${cityName}`)
                }
                jsonLd={jsonLd}
            />

            {loading && <Loader />}
            <Header context={context} />

            <main className="travel-main">
                <div className="travel-container">

                    <h1 className="tr-events-page__title">
                        {isFetes ? <Trans>Fêtes juives à</Trans> : <Trans>Événements à</Trans>}{" "}{cityName}
                    </h1>

                    {events.length === 0 ? (
                        <div className="tr-events-page__empty">
                            {isFetes
                                ? <Trans>Aucune fête programmée pour le moment.</Trans>
                                : <Trans>Aucun événement programmé pour le moment.</Trans>}
                        </div>
                    ) : (
                        <div className="tr-events-page__list">
                            {events.map(({ e: event, startMs, endMs }) => {
                                const localized = applyLang(event, lang);
                                const imgSrc = event.image ? resolveImage(event.image, ASSET_URL) : null;
                                const href = `${org.BASE_WEB_URL}/evenement/${event.urlSlug || event._id || event.id}`;
                                const isMulti = !!event.isMultiSession;
                                const isLive = !isMulti && startMs && startMs <= nowMs && (endMs || startMs) >= nowMs;
                                const periodStr = isMulti ? null : formatPeriod(startMs, endMs, lang);
                                const address = localized.adresse || localized.address?.address;
                                const links = isMulti
                                    ? []
                                    : (Array.isArray(event.links) ? event.links.filter(l => l?.url).map(l => applyLang(l, lang)) : []);

                                return (
                                    <Link key={event.id || event._id} href={href} legacyBehavior>
                                        <a className="tr-events__card">
                                            <span style={{display:"block"}} className="tr-events__imgwrap">
                                                {imgSrc ? (
                                                    <img src={imgSrc} alt={localized.name || localized.title} className="tr-events__img" loading="lazy" />
                                                ) : (
                                                    <span style={{display:"block"}} className="tr-events__img-placeholder">
                                                        {renderIcon({ name: "IconCalendarEvent", source: "tabler" }, 48)}
                                                    </span>
                                                )}

                                                {!isMulti && startMs && (
                                                    <span style={{display:"block"}} className="tr-events__datebox">
                                                        <span className="tr-events__datebox__day">{formatDay(startMs)}</span>
                                                        <span className="tr-events__datebox__month">{formatMonth(startMs, lang)}</span>
                                                    </span>
                                                )}

                                                {isLive && (
                                                    <span className="tr-events__live">
                                                        <Trans>En cours</Trans>
                                                    </span>
                                                )}
                                            </span>

                                            <span style={{display:"block"}} className="tr-events__body">
                                                <h3 className="tr-events__name">{localized.name || localized.title}</h3>

                                                <span style={{display:"block"}} className="tr-events__meta">
                                                    {isMulti && (
                                                        <span className="tr-events__meta-row">
                                                            {renderIcon({ name: "IconCalendarEvent", source: "tabler" }, 14)}
                                                            <span><Trans>Plusieurs sessions disponibles</Trans></span>
                                                        </span>
                                                    )}
                                                    {periodStr && (
                                                        <span className="tr-events__meta-row">
                                                            {renderIcon({ name: "IconClock", source: "tabler" }, 14)}
                                                            <span>{periodStr}</span>
                                                        </span>
                                                    )}
                                                    {address && (
                                                        <span className="tr-events__meta-row">
                                                            {renderIcon({ name: "IconMapPin", source: "tabler" }, 14)}
                                                            <span>{address}</span>
                                                        </span>
                                                    )}
                                                </span>

                                                {localized.description && (
                                                    <span style={{display:"block"}} className="tr-events__desc">{localized.description}</span>
                                                )}

                                                {links.length > 0 && (
                                                    <span className="tr-events__links">
                                                        {links.slice(0, 3).map((l, i) => (
                                                            <span
                                                                key={i}
                                                                role="link"
                                                                tabIndex={0}
                                                                className="tr-events__link"
                                                                onClick={(ev) => { ev.stopPropagation(); ev.preventDefault(); window.open(l.url, "_blank", "noopener,noreferrer"); }}
                                                            >
                                                                {renderIcon({ name: "IconExternalLink", source: "tabler" }, 12)}
                                                                {l.name || l.label || t`Lien`}
                                                            </span>
                                                        ))}
                                                    </span>
                                                )}

                                                <span className="tr-events__more">
                                                    <Trans>Voir plus</Trans>
                                                    {renderIcon({ name: "IconArrowRight", source: "tabler" }, 14)}
                                                </span>
                                            </span>
                                        </a>
                                    </Link>
                                );
                            })}
                        </div>
                    )}

                </div>
            </main>

            <Footer context={context} />
        </div>
    );
}
